import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { CreateTableFilterRequest } from '@omnea-app/org-settings/orgSettingsFrontendClient'
import { OrgSettingsHttpClient } from '@omnea-app/org-settings/orgSettingsHttpClient'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { TableFilterReference } from '@omnea-app/packages/universal/tables/tableFilterReference'
import { tableFiltersQueryKey, tableFiltersQueryKeys } from './tableFiltersQueryKeys'
import { transformSavedFilter } from './transformSavedFilter'

export const useCreateTableFilter = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()

  return useMutation({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [tableFiltersQueryKey, 'filter'] })
    },
    mutationFn: async (request: CreateTableFilterRequest) => {
      const token = await getToken()
      const orgSettingsClient = new OrgSettingsHttpClient({ authorization: token })
      const { data, error } = await orgSettingsClient.POST('/table-filters', {
        body: request,
      })
      throwIfApiError(error)
      return data
    },
  })
}

export const useDeleteTableFilter = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()

  return useMutation({
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: tableFiltersQueryKeys.getTableFilterById(id) })
      void queryClient.invalidateQueries({ queryKey: [tableFiltersQueryKey, 'filter'] })
    },
    mutationFn: async (id: string) => {
      const token = await getToken()
      const orgSettingsClient = new OrgSettingsHttpClient({ authorization: token })
      const { data, error } = await orgSettingsClient.POST('/table-filters/{id}/delete', {
        params: { path: { id } },
        body: {},
      })
      throwIfApiError(error)
      return data
    },
  })
}

export const useToggleTableFilterFavourite = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()

  return useMutation({
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: tableFiltersQueryKeys.getTableFilterById(id) })
      void queryClient.invalidateQueries({ queryKey: [tableFiltersQueryKey, 'filter'] })
    },
    mutationFn: async ({ id, isFavourite }: { id: string; isFavourite: boolean }) => {
      const token = await getToken()
      const orgSettingsClient = new OrgSettingsHttpClient({ authorization: token })
      const { data, error } = await orgSettingsClient.POST('/table-filters/{id}/favourite', {
        params: { path: { id } },
        body: { isFavourite },
      })
      throwIfApiError(error)
      return data
    },
  })
}

export const useGetTableFiltersByFilter = (reference?: TableFilterReference, enabled = true) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: tableFiltersQueryKeys.getTableFiltersByReference(reference),
    enabled,
    queryFn: async () => {
      const token = await getToken()
      const orgSettingsClient = new OrgSettingsHttpClient({ authorization: token })
      const { data, error } = await orgSettingsClient.GET('/table-filters', {
        params: {
          query: { reference },
        },
      })
      throwIfApiError(error)
      const filters = data.map(transformSavedFilter)
      return filters
    },
  })
}
